import React, { useState, useEffect, useContext } from 'react';
import { ArrowBack } from '@mui/icons-material/';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Button, Grid, Typography, Chip, useMediaQuery, Menu, MenuItem, Dialog } from '@mui/material';
import { Screen } from '../../components';
import { makeStyles } from '@mui/styles';
import { DeliveryDetails, OrderDetails } from './components';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import useOrders from '../../lib/hooks/useOrders';
import { OrdersContext } from '../../lib/contexts/OrdersContext';
import PrescriptionDetailsDropdown from './components/PrescriptionDetailsDropdown';
import CancelOrderPopUp from './components/CancelOrderPopUp';

const useStyles = makeStyles((theme) => ({
  screen: {
    width: '96%',
    margin: '10px auto auto',
  },
  dashboard: {
    width: '92%',
    margin: 'auto',
    '& h2': {
      width: '100%',
      fontSize: 20,
      marginTop: 20,
      marginBottom: 20,
      color: theme.palette.admin.main,
      display: 'block',
    },
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  patientLists: {
    marginTop: 20,
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  menuText: {
    float: 'right',
  },
  welcomeText: {
    marginBottom: 0,
    [theme.breakpoints.down('sm')]: {
      textAlign: 'left',
    },
  },
  titleText: {
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  overviewOptionsGrid: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  overviewInfoGrid: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
}));

const getStatusChip = (status) => {
  if (!order) return null;

  return (
    <Chip
      sx={{
        display: 'flex',
        padding: '8px 12px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        borderRadius: '20px',
        background: 'var(--color12, #1C124D)',
      }}
    >
      <Typography
        sx={{
          color: 'var(--color1, #FDFDFD)',
          leadingTrim: 'both',
          textEdge: 'cap',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: '700',
          lineHeight: 'normal',
        }}
      >
        {adjustTag(order).charAt(0).toUpperCase() + adjustTag(order).slice(1).toLowerCase()}
      </Typography>
    </Chip>
  );
};

const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case 'delivered':
      return 'success';
    case 'canceled':
      return 'error';
    case 'scheduled':
      return 'info';
    case 'shipped':
      return 'success';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
};

const OrderDetailsScreen = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { orderID } = useParams();
  const {
    state: { ordersData },
    actions: { adjustTag },
  } = useContext(OrdersContext);
  const isMobileView = useMediaQuery('(max-width:600px)');
  const [thisOrder, setThisOrder] = useState(null);
  const [selectedOption, setSelectedOption] = useState('Prescription Details');
  const [anchorEl, setAnchorEl] = useState(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  useEffect(() => {
    if (ordersData) {
      setThisOrder(orderInfo(orderID, ordersData));
    }
  }, [ordersData]);

  const handleSelectChange = (event) => {
    setSelectedOption(event.target.value);
  };

  // Function to find the order by orderID
  const orderInfo = (orderID, data) => {
    if (!data || !Array.isArray(data)) return null;

    return data.find((order) => order.id === parseInt(orderID));
  };

  // Find the order using the orderInfo function
  const order = orderInfo(orderID, ordersData);
  const [meals, setMeals] = useState(order ? order.order_items : []);
  useEffect(() => {
    if (order) {
      setMeals(order.order_items);
    }
  }, [order]);

  const handleMealsChange = (updatedMeals) => {
    setMeals(updatedMeals);
  };

  const handleBackClick = () => {
    const from = location.state?.from || '/orders'; // Default to '/orders' if no state
    navigate(from);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCancelClick = () => {
    setIsCancelDialogOpen(true);
    handleMenuClose();
  };

  const handleCancelDialogClose = () => {
    setIsCancelDialogOpen(false);
  };

  const canCancelOrder = (order) => {
    return (
      order &&
      (adjustTag(order) === 'FUTURE' || adjustTag(order) === 'PREPARING') &&
      new Date(order.cutoff_time) >= new Date()
    );
  };

  return (
    <Screen
      title={'Dashboard - Diet ID'}
      showSidebarNav={true}
      admin={true}
    >
      <Box
        id="order-details-screen-container"
        sx={{
          display: 'flex',
          width: isMobileView ? '100%' : '779px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '32px',
          margin: '0 auto',
          minHeight: '100vh',
          marginTop: isMobileView ? '40px' : '0',
        }}
      >
        <Box
          id="screen-header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            alignSelf: 'stretch',
          }}
        >
          <Box
            id="screen-header-title"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
              flex: '1 0 0',
            }}
          >
            <Box
              id="order-number"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <ArrowBack
                onClick={handleBackClick}
                sx={{
                  width: 'var(--1-5, 24px)',
                  height: 'var(--1-5, 24px)',
                }}
                style={{ cursor: 'pointer' }}
              />
              <Typography
                sx={{
                  color: 'var(--Greys-Black, #1B1B1B)',
                  fontSize: '30px',
                  fontStyle: 'normal',
                  fontWeight: '800',
                  lineHeight: '125%', // 37.5px
                }}
              >
                {`Order ${orderID}`}
              </Typography>
            </Box>
            <Chip
              label={
                <Typography
                  sx={{
                    color: thisOrder && adjustTag(thisOrder) === 'FUTURE' ? 'black' : 'white',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: '700',
                    lineHeight: 'normal',
                  }}
                >
                  {thisOrder && order
                    ? adjustTag(order).charAt(0).toUpperCase() + adjustTag(order).slice(1).toLowerCase()
                    : 'Loading...'}
                </Typography>
              }
              sx={{
                display: 'flex',
                border: thisOrder && adjustTag(thisOrder) === 'FUTURE' ? '1px solid black' : 'none',
                padding: '8px 12px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                borderRadius: '20px',
                background:
                  order &&
                  (adjustTag(order) === 'CONFIRMED' ||
                    adjustTag(order) === 'PICKED UP' ||
                    adjustTag(order) === 'APPROVED')
                    ? '#1C124D'
                    : order &&
                      (adjustTag(order) === 'FUTURE' ||
                        adjustTag(order) === 'UP NEXT' ||
                        adjustTag(order) === 'NO HUB' ||
                        adjustTag(order) === 'ASSIGNED' ||
                        adjustTag(order) === 'PLACED')
                    ? '#FDFDFD'
                    : order && (adjustTag(order) === 'CANCELED' || adjustTag(order) === 'CANCELLED')
                    ? 'var(--_Tamagui-Red-red11, #CD2B31);'
                    : 'var(--secondary-mint-accessilbe, #1D7E6B)',
              }}
            />
            {!isMobileView && (
              <Typography
                sx={{
                  color: 'var(--color9, #6B5DA8)',
                  textAlign: 'right',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  lineHeight: 'normal',
                }}
              >
                #{order?.id}
              </Typography>
            )}
          </Box>
          {!isMobileView && (
            <>
              <MoreHorizIcon
                sx={{
                  width: 'var(--1-5, 24px)',
                  height: '24px',
                  cursor: 'pointer',
                  opacity: 0,
                }}
                // disabled={(adjustTag(order) === 'CANCELED' || adjustTag(order) === 'CANCELLED') || ((adjustTag(order) === 'FUTURE' || adjustTag(order) === 'PREPARING') && new Date(order.cutoff_time) < new Date())}
                // onClick={canCancelOrder(order) ? handleMenuOpen : null}
              />
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleCancelClick}>
                  <Typography
                    sx={{
                      color: 'var(--color12, #1C124D)',
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: '700',
                      lineHeight: 'normal',
                    }}
                  >
                    Cancel Order
                  </Typography>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        <Box
          id="screen-body"
          sx={{
            display: 'flex',
            flexDirection: isMobileView ? 'column' : 'row',
            alignItems: isMobileView ? 'center' : 'flex-start',
            gap: '32px',
            alignSelf: 'stretch',
          }}
        >
          {order && <DeliveryDetails delivery={order} />}
          {order && (
            <OrderDetails
              order={{ ...order, meals }}
              allowShowReportIssueButton={true}
              showPopUps={true}
              onMealsChange={handleMealsChange}
              showFull={true}
            />
          )}
        </Box>
      </Box>

      <CancelOrderPopUp
        open={isCancelDialogOpen}
        orderID={orderID}
        onClose={handleCancelDialogClose}
      />
    </Screen>
  );
};

export default OrderDetailsScreen;
